import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthDialog } from "@/components/AuthDialog";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Check, X, Crown, Loader2, Sparkles, Skull } from "lucide-react";

const FREE_FEATURES = [
  { t: "Random build generator", ok: true },
  { t: "Basic video ideas", ok: true },
  { t: "Basic challenges", ok: true },
  { t: "10 AI generations / day", ok: true },
  { t: "Save up to 5 ideas", ok: true },
  { t: "Personalized channel ideas", ok: false },
  { t: "Creator analytics", ok: false },
  { t: "Custom challenge builder", ok: false },
];

const PRO_FEATURES = [
  "Unlimited AI generations",
  "Advanced video idea generator",
  "Personalized ideas from your channel style",
  "Unlimited saved builds & ideas",
  "Creator analytics dashboard",
  "Custom challenge creation",
  "Pro badge on your profile",
];

export default function Pricing() {
  const { user, refreshUser, setUser } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    api.get("/subscription/plans").then(({ data }) => setPlans(data.plans)).catch(() => {});
    if (user) refreshUser();
    // eslint-disable-next-line
  }, []);

  const subscribe = async (plan) => {
    if (!user) return setAuthOpen(true);
    setLoading(plan);
    try {
      const { data } = await api.post("/checkout/subscription", { plan, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout.");
      setLoading(null);
    }
  };

  const cancelSubscription = async () => {
    setCancelling(true);
    try {
      const { data } = await api.post("/subscription/cancel");
      setUser(data);
      setCancelOpen(false);
      toast.success("Your Pro membership has been cancelled.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel. Try again.");
    } finally {
      setCancelling(false);
    }
  };

  const isPro = user?.is_pro;

  return (
    <div data-testid="pricing-page" className="mx-auto max-w-5xl px-6 py-16 md:px-10">
      <div className="text-center">
        <div className="mb-4 inline-flex items-center gap-2 border border-primary/30 bg-primary/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.25em] text-primary">
          <Crown className="h-3.5 w-3.5" /> Creator Pro
        </div>
        <h1 className="font-display text-5xl tracking-wider md:text-7xl">Level up your <span className="text-primary text-glow-red">channel</span></h1>
        <p className="mx-auto mt-4 max-w-xl text-foreground/60">Unlock unlimited AI generations, personalized ideas, analytics and more. Cancel anytime.</p>
        {isPro && (
          <div className="mt-5 flex flex-col items-center gap-3">
            <div className="inline-flex items-center gap-2 border border-primary/40 bg-primary/10 px-4 py-2 text-sm font-bold uppercase tracking-widest text-primary">
              <Crown className="h-4 w-4" /> You're a Pro creator
              {user?.pro_until && <span className="text-foreground/50"> · until {new Date(user.pro_until).toLocaleDateString()}</span>}
            </div>
            <button data-testid="cancel-pro-btn" onClick={() => setCancelOpen(true)} className="text-xs font-bold uppercase tracking-widest text-foreground/50 underline-offset-4 hover:text-primary hover:underline">
              Cancel membership
            </button>
          </div>
        )}
      </div>

      <div className="mt-12 grid gap-6 md:grid-cols-2">
        {/* FREE */}
        <div className="border border-border bg-card p-8">
          <div className="flex items-center gap-2 text-foreground/70"><Skull className="h-5 w-5" /><span className="text-xs font-bold uppercase tracking-widest">Free</span></div>
          <p className="mt-4 font-display text-5xl">$0<span className="text-lg text-foreground/40">/forever</span></p>
          <ul className="mt-6 space-y-3">
            {FREE_FEATURES.map((f) => (
              <li key={f.t} className={`flex items-center gap-3 text-sm ${f.ok ? "text-foreground/80" : "text-foreground/35"}`}>
                {f.ok ? <Check className="h-4 w-4 text-foreground/50" /> : <X className="h-4 w-4 text-foreground/25" />}{f.t}
              </li>
            ))}
          </ul>
          <Button disabled variant="outline" className="mt-8 w-full rounded-sm border-white/10 bg-transparent uppercase tracking-widest">Current Plan</Button>
        </div>

        {/* PRO */}
        <div className="relative overflow-hidden border-2 border-primary bg-card p-8">
          <div className="absolute -right-12 top-6 rotate-45 bg-primary px-12 py-1 text-[10px] font-bold uppercase tracking-widest text-primary-foreground">Best Value</div>
          <div className="flex items-center gap-2 text-primary"><Crown className="h-5 w-5" /><span className="text-xs font-bold uppercase tracking-widest">Creator Pro</span></div>

          <div className="mt-4 flex items-end gap-3">
            <p className="font-display text-5xl text-primary">${plans?.monthly?.amount ?? "4.99"}<span className="text-lg text-foreground/40">/mo</span></p>
          </div>
          <p className="mt-1 text-sm text-foreground/50">or ${plans?.yearly?.amount ?? "49"}/year — save ~18%</p>

          <ul className="mt-6 space-y-3">
            {PRO_FEATURES.map((f) => (
              <li key={f} className="flex items-center gap-3 text-sm text-foreground/85"><Check className="h-4 w-4 text-primary" />{f}</li>
            ))}
          </ul>

          <div className="mt-8 grid gap-3 sm:grid-cols-2">
            <Button data-testid="subscribe-monthly-btn" disabled={isPro || loading} onClick={() => subscribe("monthly")} className="rounded-sm bg-primary py-6 font-bold uppercase tracking-widest hover:bg-primary/90">
              {loading === "monthly" ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Sparkles className="mr-2 h-4 w-4" /> ${plans?.monthly?.amount ?? "4.99"}/mo</>}
            </Button>
            <Button data-testid="subscribe-yearly-btn" disabled={isPro || loading} onClick={() => subscribe("yearly")} variant="outline" className="rounded-sm border-primary/50 bg-transparent py-6 font-bold uppercase tracking-widest hover:bg-primary/10">
              {loading === "yearly" ? <Loader2 className="h-5 w-5 animate-spin" /> : <>${plans?.yearly?.amount ?? "49"}/yr</>}
            </Button>
          </div>
          <p className="mt-3 text-center text-xs text-foreground/40">Secure checkout powered by Stripe · Cancel anytime</p>
        </div>
      </div>

      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="max-w-md rounded-sm border-white/10 bg-card" data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl tracking-wider">Cancel Creator Pro?</DialogTitle>
            <DialogDescription className="text-sm text-foreground/60">
              Your Pro benefits (unlimited generations, analytics, custom challenges and personalized ideas) will end immediately and you'll return to the Free plan. You can resubscribe anytime.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setCancelOpen(false)} className="rounded-sm border-white/10 bg-transparent uppercase tracking-widest">Keep Pro</Button>
            <Button data-testid="confirm-cancel-btn" onClick={cancelSubscription} disabled={cancelling} className="rounded-sm bg-primary uppercase tracking-widest hover:bg-primary/90">
              {cancelling ? <Loader2 className="h-4 w-4 animate-spin" /> : "Cancel Membership"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
